import { useCallback } from 'react';
import {
  FlowResourceType,
  IValidationResult,
  useEditorStore,
  useFlowBuilder2Store,
  useFlowBuilder2FlowValidators,
  useFlowBuilder2StepValidators,
  useFlowBuilder2TaskValidators,
  useFlowBuilder2ActionValidators,
  useLayoutComponentSettings,
  useLayoutComponentPropertyEditorMap,
  LayoutComponentNames,
} from '@diana/core';
import { first, uniqBy } from 'lodash';

const getRecordEntries = <K extends string, V>(record: Record<K, V>) => {
  return Array.from(Object.entries(record)) as [K, V][];
};

export const useValidate = () => {
  const flowValidators = useFlowBuilder2FlowValidators();
  const stepValidators = useFlowBuilder2StepValidators();
  const taskValidators = useFlowBuilder2TaskValidators();
  const actionValidators = useFlowBuilder2ActionValidators();

  const { getSettings } = useLayoutComponentSettings();
  const propertyEditorMap = useLayoutComponentPropertyEditorMap();

  const getFlow = useEditorStore((s) => s.getFlow);
  const getLayoutById = useEditorStore((s) => s.getLayoutById);
  const setFieldState = useFlowBuilder2Store((s) => s.setFieldState);

  return useCallback(() => {
    const flow = getFlow();
    const results: IValidationResult[] = [];

    if (flow) {
      // validate flow
      for (const [fieldName, validate] of getRecordEntries(flowValidators)) {
        const validationResult = validate({ flow });
        if (validationResult) results.push(validationResult);
        setFieldState(flow.id, 'flow', fieldName, {
          dirty: true,
          touched: true,
          validationResult,
        });
      }

      // validate steps
      const steps = uniqBy(flow.steps, (s) => s.stepId);
      for (const step of steps) {
        for (const [fieldName, validate] of getRecordEntries(stepValidators)) {
          const validationResult = validate({ flow, step });
          if (validationResult) results.push(validationResult);
          setFieldState(step.stepId, 'step', fieldName, {
            dirty: true,
            touched: true,
            validationResult,
          });

          // validate tasks
          const tasks = uniqBy(
            flow.steps.filter((s) => s.stepId === step.stepId),
            (s) => s.taskId,
          );
          for (const task of tasks) {
            for (const [fieldName, validate] of getRecordEntries(taskValidators)) {
              const validationResult = validate({ flow, task });
              if (validationResult) results.push(validationResult);
              setFieldState(task.taskId, 'task', fieldName, {
                dirty: true,
                touched: true,
                validationResult,
              });
            }

            // validate action sets
            const actionSets = uniqBy(
              flow.steps.filter((s) => s.stepId === step.stepId && s.taskId === task.taskId),
              (s) => s.actionSetId,
            );
            for (const actionSet of actionSets) {
              // validate actions
              const actions = actionSet.actions ?? [];
              for (const action of actions) {
                for (const [fieldName, validate] of getRecordEntries(actionValidators)) {
                  const validationResult = validate({ flow, actionSet, action });
                  if (validationResult) results.push(validationResult);
                  setFieldState(action.id, 'action', fieldName, {
                    dirty: true,
                    touched: true,
                    validationResult,
                  });
                }
              }
            }

            // validate layout components
            const layout = getLayoutById(task.layoutId);
            if (layout) {
              for (let i = 0; i < layout.components.length; i++) {
                const component = layout.components[i];
                const editors = getSettings(component)?.editors ?? [];

                if (flow.properties.flowResourceType === FlowResourceType.Entity) {
                  if (i === 0 && component.component === LayoutComponentNames.SECTION) {
                    continue;
                  }
                }

                for (const editor of editors) {
                  const { validate } = propertyEditorMap[editor.name];
                  const validationResult = validate({
                    flowId: flow.id,
                    layoutId: task.layoutId,
                    component,
                    componentIndex: i,
                    options: editor.options ?? {},
                  });
                  if (validationResult) results.push(validationResult);
                  setFieldState(component.id, 'layoutComponent', editor.name, {
                    dirty: true,
                    touched: true,
                    validationResult,
                  });
                }
              }
            }
          }
        }
      }
    }

    return first(results) ?? null;
  }, [
    getFlow,
    getLayoutById,
    setFieldState,
    flowValidators,
    stepValidators,
    taskValidators,
    actionValidators,
    getSettings,
    propertyEditorMap,
  ]);
};
